import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';

import {
    ResourceAbsence,
    ResourceAbsenceApprover,
    ResourceAbsenceApproverCandidate,
    ResourceAbsencePermissions,
    ResourceAbsencePlanning,
    ResourceAbsenceRequest,
    ResourceCapacityConflict,
    ResourceCapacityQuery,
    ResourceCapacityReport,
    ResourceEmployeeProfile,
    ResourceEmployeeProfileUpdate,
    ResourceMonitorOverview,
    ResourceTeam,
    ResourceMyTeamPermissions,
    ResourceTeamSave,
    ResourceUserOption
} from '../Models/resource-modules.models';
import { ResourceCategory } from '../Models/resource.models';

/**
 * Kapazitätsplanung der Ressourcenplanung (/api/resourceplanning/capacity).
 * Alle Werte werden im Backend berechnet; das Frontend zeigt sie nur an.
 */
@Injectable({ providedIn: 'root' })
export class ResourceCapacityApiService {
    private readonly apiUrl = environment.apiBaseUrl + '/resourceplanning/capacity';

    constructor(private http: HttpClient) { }

    getReport(query: ResourceCapacityQuery): Observable<ResourceCapacityReport> {
        return this.http.get<ResourceCapacityReport>(`${this.apiUrl}/report`, { params: this.params(query) });
    }

    getDays(query: ResourceCapacityQuery): Observable<ResourceCapacityReport> {
        return this.http.get<ResourceCapacityReport>(`${this.apiUrl}/days`, { params: this.params(query) });
    }

    getTeamReport(teamId: number, query: ResourceCapacityQuery): Observable<ResourceCapacityReport> {
        return this.http.get<ResourceCapacityReport>(`${this.apiUrl}/teams/${teamId}`, { params: this.params(query) });
    }

    getConflicts(query: ResourceCapacityQuery): Observable<ResourceCapacityConflict[]> {
        return this.http.get<ResourceCapacityConflict[]>(`${this.apiUrl}/conflicts`, { params: this.params(query) });
    }

    private params(query: ResourceCapacityQuery): HttpParams {
        let params = new HttpParams().set('from', query.from).set('to', query.to);
        if (query.granularity) {
            params = params.set('granularity', query.granularity);
        }
        if (query.teamId) {
            params = params.set('teamId', query.teamId);
        }
        if (query.includeDays) {
            params = params.set('includeDays', true);
        }
        for (const id of query.userIds ?? []) {
            params = params.append('userIds', id);
        }
        for (const id of query.skillIds ?? []) {
            params = params.append('skillIds', id);
        }
        return params;
    }
}

/**
 * Abwesenheiten/Urlaub (/api/resourceplanning/absences). Gemeinsame Datenbasis mit dem
 * User-Bereich – Anträge und Statuswerte sind in beiden Oberflächen identisch.
 */
@Injectable({ providedIn: 'root' })
export class ResourceAbsenceApiService {
    private readonly apiUrl = environment.apiBaseUrl + '/resourceplanning/absences';

    constructor(private http: HttpClient) { }

    getPermissions(): Observable<ResourceAbsencePermissions> {
        return this.http.get<ResourceAbsencePermissions>(`${this.apiUrl}/permissions`);
    }

    getCategories(): Observable<ResourceCategory[]> {
        return this.http.get<ResourceCategory[]>(`${this.apiUrl}/categories`);
    }

    getMine(): Observable<ResourceAbsence[]> {
        return this.http.get<ResourceAbsence[]>(`${this.apiUrl}/mine`);
    }

    /** Alle Anträge (nur Genehmiger). from/to als yyyy-MM-ddTHH:mm:ss, status Pending|Approved|Rejected. */
    getAll(from?: string, to?: string, status?: string): Observable<ResourceAbsence[]> {
        let params = new HttpParams();
        if (from) { params = params.set('from', from); }
        if (to) { params = params.set('to', to); }
        if (status) { params = params.set('status', status); }
        return this.http.get<ResourceAbsence[]>(this.apiUrl, { params });
    }

    getPending(): Observable<ResourceAbsence[]> {
        return this.http.get<ResourceAbsence[]>(`${this.apiUrl}/pending`);
    }

    get(id: number): Observable<ResourceAbsence> {
        return this.http.get<ResourceAbsence>(`${this.apiUrl}/${id}`);
    }

    request(payload: ResourceAbsenceRequest): Observable<ResourceAbsence> {
        return this.http.post<ResourceAbsence>(this.apiUrl, payload);
    }

    cancel(id: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${id}`);
    }

    approve(id: number): Observable<ResourceAbsence> {
        return this.http.post<ResourceAbsence>(`${this.apiUrl}/${id}/approve`, {});
    }

    reject(id: number): Observable<ResourceAbsence> {
        return this.http.post<ResourceAbsence>(`${this.apiUrl}/${id}/reject`, {});
    }

    /** Persönlicher Urlaubsgenehmiger (Vorauswahl + manuelle Festlegung). */
    getApprover(): Observable<ResourceAbsenceApprover> {
        return this.http.get<ResourceAbsenceApprover>(`${this.apiUrl}/approver`);
    }

    /** Genehmiger manuell festlegen – bleibt auch bei Teamwechseln bestehen. */
    setApprover(approverUserId: string): Observable<ResourceAbsenceApprover> {
        return this.http.put<ResourceAbsenceApprover>(`${this.apiUrl}/approver`, { approverUserId });
    }

    /** Zurück zur automatischen Ermittlung aus der Teamzuordnung. */
    resetApprover(): Observable<ResourceAbsenceApprover> {
        return this.http.delete<ResourceAbsenceApprover>(`${this.apiUrl}/approver`);
    }

    /** Auswählbare Genehmiger (aktive Benutzer ohne das eigene Konto). */
    getApproverCandidates(): Observable<ResourceAbsenceApproverCandidate[]> {
        return this.http.get<ResourceAbsenceApproverCandidate[]>(`${this.apiUrl}/approver/candidates`);
    }

    /** Genehmigte Abwesenheiten als schreibgeschützte Planungsdatensätze (yyyy-MM-ddTHH:mm:ss). */
    getPlanning(from: string, to: string, userIds?: string[]): Observable<ResourceAbsencePlanning[]> {
        let params = new HttpParams().set('from', from).set('to', to);
        for (const id of userIds ?? []) {
            params = params.append('userIds', id);
        }
        return this.http.get<ResourceAbsencePlanning[]>(`${this.apiUrl}/planning`, { params });
    }
}

/** Teams und Mitarbeiterprofile (/api/resourceplanning/teams, /api/resourceplanning/employees). */
@Injectable({ providedIn: 'root' })
export class ResourceTeamApiService {
    private readonly teamsUrl = environment.apiBaseUrl + '/resourceplanning/teams';
    private readonly employeesUrl = environment.apiBaseUrl + '/resourceplanning/employees';

    constructor(private http: HttpClient) { }

    getMyTeamPermissions(): Observable<ResourceMyTeamPermissions> {
        return this.http.get<ResourceMyTeamPermissions>(`${this.teamsUrl}/permissions/me`);
    }

    changeLeader(id: number, userId: string): Observable<ResourceTeam> {
        return this.http.put<ResourceTeam>(`${this.teamsUrl}/${id}/leader`, { userId });
    }

    /** Hauptteam setzen; ohne userId für sich selbst (für andere nur SuperAdmin/Kaufmännisch). */
    setPrimaryTeam(id: number, userId?: string): Observable<{ teamId: number; isPrimaryTeam: boolean }> {
        let params = new HttpParams();
        if (userId) { params = params.set('userId', userId); }
        return this.http.put<{ teamId: number; isPrimaryTeam: boolean }>(`${this.teamsUrl}/${id}/primary`, {}, { params });
    }

    /** Benutzerauswahl für Teammitglieder (für alle, die Teams anlegen oder leiten). */
    getUserOptions(): Observable<ResourceUserOption[]> {
        return this.http.get<ResourceUserOption[]>(`${this.teamsUrl}/user-options`);
    }

    getTeams(includeInactive = false): Observable<ResourceTeam[]> {
        const params = new HttpParams().set('includeInactive', includeInactive);
        return this.http.get<ResourceTeam[]>(this.teamsUrl, { params });
    }

    getTeam(id: number): Observable<ResourceTeam> {
        return this.http.get<ResourceTeam>(`${this.teamsUrl}/${id}`);
    }

    createTeam(payload: ResourceTeamSave): Observable<ResourceTeam> {
        return this.http.post<ResourceTeam>(this.teamsUrl, payload);
    }

    updateTeam(id: number, payload: ResourceTeamSave): Observable<ResourceTeam> {
        return this.http.put<ResourceTeam>(`${this.teamsUrl}/${id}`, payload);
    }

    deleteTeam(id: number): Observable<void> {
        return this.http.delete<void>(`${this.teamsUrl}/${id}`);
    }

    addMembers(id: number, userIds: string[]): Observable<ResourceTeam> {
        return this.http.post<ResourceTeam>(`${this.teamsUrl}/${id}/members`, { userIds });
    }

    removeMember(id: number, userId: string): Observable<ResourceTeam> {
        return this.http.delete<ResourceTeam>(`${this.teamsUrl}/${id}/members/${encodeURIComponent(userId)}`);
    }

    /** Alle Benutzerkonten mit Planungsprofil (Verwaltung); onlyInPlanning = nur Mitarbeiter der Gesamtplanung. */
    getEmployeeProfiles(onlyInPlanning = false): Observable<ResourceEmployeeProfile[]> {
        const params = new HttpParams().set('onlyInPlanning', onlyInPlanning);
        return this.http.get<ResourceEmployeeProfile[]>(`${this.employeesUrl}/all`, { params });
    }

    /** Bestehende Benutzerkonten manuell (teamunabhängig) in die Gesamtplanung aufnehmen (SuperAdmin/Kaufmännisch). */
    addToPlanning(userIds: string[]): Observable<ResourceEmployeeProfile[]> {
        return this.http.post<ResourceEmployeeProfile[]>(`${this.employeesUrl}/planning`, { userIds });
    }

    /** Manuelle Aufnahme aufheben – löscht keine Planungsdaten; Teammitglieder bleiben über ihr Team in der Planung. */
    removeFromPlanning(userId: string): Observable<ResourceEmployeeProfile> {
        return this.http.delete<ResourceEmployeeProfile>(`${this.employeesUrl}/${encodeURIComponent(userId)}/planning`);
    }

    getEmployeeProfile(userId: string): Observable<ResourceEmployeeProfile> {
        return this.http.get<ResourceEmployeeProfile>(`${this.employeesUrl}/${encodeURIComponent(userId)}/profile`);
    }

    /** Nur SuperAdmin (serverseitig geprüft). */
    updateEmployeeProfile(userId: string, payload: ResourceEmployeeProfileUpdate): Observable<ResourceEmployeeProfile> {
        return this.http.put<ResourceEmployeeProfile>(`${this.employeesUrl}/${encodeURIComponent(userId)}/profile`, payload);
    }
}

/** Monitor-Modus (/api/resourceplanning/monitor) – eine Antwort, gedacht für regelmäßiges Neuladen. */
@Injectable({ providedIn: 'root' })
export class ResourceMonitorApiService {
    private readonly apiUrl = environment.apiBaseUrl + '/resourceplanning/monitor';

    constructor(private http: HttpClient) { }

    getOverview(date?: string, horizonDays = 7, teamId?: number | null): Observable<ResourceMonitorOverview> {
        let params = new HttpParams().set('horizonDays', horizonDays);
        if (date) { params = params.set('date', date); }
        if (teamId) { params = params.set('teamId', teamId); }
        return this.http.get<ResourceMonitorOverview>(this.apiUrl, { params });
    }
}
